<template>
	<div>
		<div class="row gutter-sm q-mb-lg">
			<div class="col-xs-12 col-sm-12">
				<q-field :count="256">
					<q-input v-model="title" ref="title" :float-label="$t('title')" :error="$v.title.$error"
						@input="$v.title.$touch" />
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<p class="caption" :class="$v.description.$error ? 'text-negative' : ''">
					{{ $t('introduction') }}
				</p>

				<q-editor v-model="description" ref="description" :error="$v.description.$error"
					@input="$v.description.$touch" :toolbar="[
					['bold', 'italic', 'strike', 'underline'],
					['left', 'center', 'right', 'justify'],
					['link', 'upload'],
					[
						{
							label: null,
							icon: 'fas fa-font',
							fixedLabel: true,
							fixedIcon: true,
							list: 'no-icons',
							options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
						},
						{
							label: null,
							icon: 'fas fa-text-height',
							fixedLabel: true,
							fixedIcon: true,
							list: 'no-icons',
							options: [
								'size-1',
								'size-2',
								'size-3',
								'size-4',
								'size-5',
								'size-6',
								'size-7',
							],
						},
						'removeFormat',
					],
					['quote', 'unordered', 'ordered'],
				]" :definitions="{
					bold: { label: null, icon: 'fas fa-bold' },
					italic: { label: null, icon: 'fas fa-italic' },
					strike: { label: null, icon: 'fas fa-strikethrough' },
					underline: { label: null, icon: 'fas fa-underline' },
					left: { label: null, icon: 'fas fa-align-left' },
					center: { label: null, icon: 'fas fa-align-center' },
					right: { label: null, icon: 'fas fa-align-right' },
					justify: { label: null, icon: 'fas fa-align-justify' },
					link: { label: null, icon: 'fas fa-link' },
					removeFormat: { label: null, icon: 'fas fa-eraser' },
					quote: { label: null, icon: 'fas fa-quote-right' },
					unordered: { label: null, icon: 'fas fa-list-ul' },
					ordered: { label: null, icon: 'fas fa-list-ol' },
				}">
				</q-editor>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-select v-model="survey_type" ref="survey_type" :options="survey_types.map((s) => {
					return { label: this.$t(s.label), value: s.value }
				})
					" :float-label="$t('survey_type')" :error="$v.survey_type.$error" @input="$v.survey_type.$touch" />
			</div>

			<div class="col-xs-12 col-sm-6" v-if="selected_client_params.group_training_use_score">
				<q-input v-model="score" ref="score" :float-label="$t('score')" type="number" :error="$v.score.$error"
					@input="$v.score.$touch" v-if="!selected_client_params.group_training_use_static_score" />

				<q-select v-model="score" ref="score" :options="scores" :float-label="$t('score')"
					:error="$v.score.$error" @input="$v.score.$touch"
					v-if="selected_client_params.group_training_use_static_score" />
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-datetime type="date" format="DD/MM/YYYY" v-model="start_date" :float-label="$t('start_date')"
					clearable />
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-datetime type="date" format="DD/MM/YYYY" v-model="end_date" :float-label="$t('end_date')"
					clearable />
			</div>

			<div class="col-12">
				<q-select v-model="timezone" ref="timezone" :options="timezones" :float-label="$t('timezone')" filter
					:error="$v.timezone.$error" @input="$v.timezone.$touch"  />
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select v-model="tag" ref="tag" :options="optionTags" :float-label="$t('tags')" :error="$v.tag.$error"
					@input="$v.tag.$touch" multiple chips filter autofocus-filter />
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-checkbox class="q-mb-sm" v-model="show_feedback" :val="true" :label="$t('show_feedback')" />
			</div>
			<div class="col-xs-12 col-sm-12">
				<q-checkbox class="q-mb-sm" v-model="anonyma_results" :val="true" :label="$t('anonyma_results')" :disable="is_anonymous_survey_locked" />
			</div>
		</div>

		<q-tabs v-model="tab" color="secondary" class="shadow-1">
			<q-tab slot="title" name="permissions" icon="fas fa-clipboard-check" :label="$t('permissions')" />

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('areas') }}</p>
						<q-checkbox class="q-mb-sm" v-model="all_areas" :val="true" :label="$t('select_all_areas')"
							@input="checkAllAreas()" />
						<select-multiple label="" :filters="areas" :options="areas_list" @on_value="areas = $event"
							:error="$v.areas.$error" :input="$v.areas.$touch" />
					</div>

					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox v-model="all_profiles" :val="true" :label="$t('all')" @input="checkAll()" />
							</div>

							<div class="col-xs-12 col-sm-4" v-for="(item, index) in profiles_list" :key="index"
								:class="getErrorClass($v.profiles.$error)">
								<q-checkbox keep-color v-model="profiles" ref="profiles" :val="item.value"
									:label="item.label" @input="$v.profiles.$touch"
									:color="getErrorColor($v.profiles.$error)" />
							</div>

							<div class="col-xs-12 col-sm-12">
								<q-select v-model="user_groups" ref="user_groups" :options="user_groups_list"
									:float-label="$t('groups')" :error="$v.user_groups.$error"
									@input="$v.user_groups.$touch" multiple chips />
							</div>
						</div>
					</div>
					<div class="col-xs-12 col-sm-11">
						<q-input v-model="url_survey_portal" :float-label="$t('link')" :disable="true" />
					</div>

					<div class="col-xs-12 col-sm-1 q-mt-lg">
						<q-btn dense color="primary" icon="far fa-copy" class="full-width" @click="copyLink()">
							<q-tooltip>
								{{ $t('copy_link') }}
							</q-tooltip>
						</q-btn>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<div class="row gutter-sm q-mt-xs">
			<div class="col-12">
				<q-chips-input v-model="owners_list" :float-label="$t('owners')" @remove="removeOwner">
					<q-autocomplete @search="searchOwner" @selected="selectOwner" :min-characters="3"
						:value-field="(v) => `${v.full_name} - ${v.email}`" />
				</q-chips-input>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn color="tertiary" class="full-width" :label="$t('back')" icon="fas fa-chevron-left"
					@click="$router.push({ name: 'admin_surveys' })" />
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn color="secondary" class="full-width" :label="$t('save')" icon="far fa-save"
					@click="saveData()" />
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, maxValue, minValue, maxLength } from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import SelectMultiple from 'components/utils/SelectMultiple'
export default {
	i18nOptions: { namespaces: ['custom', 'survey', 'admin', 'common'] },
	data() {
		return {
			survey_id: 0,
			areas: [],
			description: '',
			end_date: null,
			owners: [],
			profiles: [],
			score: 0,
			start_date: null,
			survey_type: '',
			title: '',
			user_groups: [],
			tab: 'permissions',
			all_profiles: false,
			selected_client_params: [],
			owners_list: [],
			all_areas: false,
			tag: [],
			show_feedback: false,
			anonyma_results: false,
			is_anonymous_survey_locked: false,
			timezone: '',
		}
	},

	components: {
		'select-multiple': SelectMultiple,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			areas_list: 'registration_lists/country_areas',
			user_groups_list: 'registration_lists/group_users',
			profiles_list: 'registration_lists/profiles',
			survey_types: 'registration_lists/survey_types',
			user_id: 'common/id',
			tags: 'registration_lists/tags',
			user_full_name: 'common/full_name',
			user_email: 'common/email',
			scores: 'registration_lists/group_training_scores',
			timezones: 'registration_lists/timezones',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		optionTags() {
			if (this.tags.length > 0) {
				return this.tags.map((t) => {
					let areas = t.areas.map((a) => a.name)
					return {
						value: t.value,
						label: t.label,
						sublabel: areas.join(', '),
					}
				})
			} else return []
		},

		url_survey_portal() {
			let url = process.env.SITE_URL ? process.env.SITE_URL : document.location.origin
			return `${url}/home/surveys/${this.survey_id}`
		},
	},

	methods: {
		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_surveys/surveys/${this.survey_id}/`, this.getHeader)
				.then((response) => {
					this.areas = response.data.areas
					this.description = response.data.description
					this.end_date = response.data.end_date
						? moment(response.data.end_date)
							.tz('Etc/GMT')
							.format('YYYY-MM-DDTHH:mm:ss.SSS')
						: null
					this.owners = response.data.owners
					this.profiles = response.data.profiles
					this.tag = response.data.tags
					this.score = response.data.score
					this.start_date = response.data.start_date
						? moment(response.data.start_date)
							.tz('Etc/GMT')
							.format('YYYY-MM-DDTHH:mm:ss.SSS')
						: null
					this.timezone = response.data.timezone
					this.survey_type = response.data.survey_type
					this.title = response.data.title
					this.user_groups = response.data.user_groups
					this.show_feedback = response.data.show_feedback
					this.setAnonymousSurveyState(response.data.is_anonymous)
					response.data.owners_list.map((o) => {
						this.owners_list.push(`${o.full_name} - ${o.email}`)
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				const data = {
					title: this.title,
					description: this.description,
					survey_type: this.survey_type,
					show_feedback: this.show_feedback,
					is_anonymous: this.is_anonymous_survey_locked || this.anonyma_results,
					score: this.selected_client_params.group_training_use_score
						? this.score
						: 0,
					tags: this.tag,
					start_date: this.start_date
						? date.formatDate(this.start_date, 'YYYY-MM-DD')
						: null,
					end_date: this.end_date
						? date.formatDate(this.end_date, 'YYYY-MM-DD')
						: null,
					timezone: this.timezone || null,
					areas:
						this.areas.length > 0 && this.profiles.length > 0
							? this.areas
							: [],
					profiles:
						this.areas.length > 0 && this.profiles.length > 0
							? this.profiles
							: [],
					user_groups:
						this.user_groups.length > 0
							? this.user_groups
							: [],
					owners: this.owners,
				}

				if (this.survey_id != 0) {
					this.$axios
						.put(`/admin_surveys/surveys/${this.survey_id}/`, data, this.getHeader)
						.then((response) => {
							this.lockAnonymousSurvey(data.is_anonymous)
							this.$q.notify({
								message: this.$t('survey_save_success'),
								type: 'positive',
								icon: 'far fa-save',
							})
						})
						.catch((e) => {
							console.log(e)

							this.$q.notify({
								message: this.$t('survey_save_error'),
								type: 'negative',
								icon: 'fas fa-ban',
							})
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				} else {
					this.$axios
						.post(`/admin_surveys/surveys/`, data, this.getHeader)
						.then((response) => {
							this.survey_id = response.data.id
							this.lockAnonymousSurvey(data.is_anonymous)
							this.$router.push({
								name: 'admin_survey',
								params: { id: response.data.id },
							})

							this.$q.notify({
								message: this.$t('survey_save_success'),
								type: 'positive',
								icon: 'far fa-save',
							})
						})
						.catch((e) => {
							console.log(e)

							this.$q.notify({
								message: this.$t('survey_save_error'),
								type: 'negative',
								icon: 'fas fa-ban',
							})
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		searchOwner(terms, done) {
			this.$axios
				.get(`/accounts/get_owners/?search=${terms}`, this.getHeader)
				.then((response) => {
					let users = []

					response.data.forEach((element) => {
						users.push({
							value: element.id,
							label: `${element.full_name} - ${element.email}`,
							full_name: element.full_name,
							email: element.email,
							id: element.id,
						})
					})

					done(users)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		selectOwner(item, keyboard) {
			if (!keyboard) this.owners.push(item.id)
		},

		removeOwner({ index, value }) {
			this.owners.splice(index, 1)
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles_list.forEach((element) => {
					this.profiles.push(element.value)
				})
			} else {
				this.profiles = []
			}
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.areas = this.areas_list.map((e) => e.value)
			} else {
				this.areas = []
			}
		},

		getErrorColor(state) {
			if (state) return 'negative'
			else return ''
		},

		getErrorClass(state) {
			if (state) return 'checkbox-error'
			else return ''
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		copyLink() {
			this.$copyText(this.url_survey_portal).then(
				(e) => {
					this.$q.notify({
						message: this.$t('link_copied_to_clipboard'),
						type: 'positive',
						icon: 'far fa-copy',
					})
				},
				(e) => {
					console.log(e)
				}
			)
		},

		setAnonymousSurveyState(isAnonymous) {
			const normalizedIsAnonymous = Boolean(isAnonymous)
			this.anonyma_results = normalizedIsAnonymous
			this.lockAnonymousSurvey(normalizedIsAnonymous)
		},

		lockAnonymousSurvey(isAnonymous) {
			this.is_anonymous_survey_locked = Boolean(isAnonymous)
		},

		fetchTimezones() {
			let values = []

			moment.tz
				.names()
				.reduce((memo, tz) => {
					memo.push({
						name: tz,
						offset: moment.tz(tz).utcOffset(),
					})

					return memo
				}, [])
				.sort((a, b) => {
					return a.offset - b.offset
				})
				.reduce((memo, tz) => {
					const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
					values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
				}, '')

			this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
		},

		getCurrentTimezone() {
			return date.formatDate(new Date(), 'Z')
		},
	},

	created() {
		this.survey_id = this.$route.params.id
		if (this.survey_id != 0) this.fetchData()
		else {
			this.owners.push(this.user_id)
			this.owners_list.push(`${this.user_full_name} - ${this.user_email}`)
		}

		let client = this.clients.find((obj) => {
			return obj.id === this.id_user_client_selected
		})
		this.selected_client_params = client ? client : {}
		this.fetchTimezones()
		if (this.survey_id == 0) this.timezone = moment.tz.guess()
	},

	validations() {
		return {
			title: { required, maxLength: maxLength(256) },
			description: { required },
			survey_type: { required },
			tag: { required },
			areas: {
				required: requiredIf(function () {
					return this.user_groups.length === 0 || this.profiles.length > 0
				}),
			},
			
			profiles: {
				required: requiredIf(function () {
					return this.user_groups.length === 0 || this.areas.length > 0
				}),
			},
			
			user_groups: {
				required: requiredIf(function () {
					return this.areas.length === 0 && this.profiles.length === 0
				}),
			},
			score: {
				maxValue: maxValue(99999),
				minValue: minValue(0),
			},
			timezone: {
				requiredIf: requiredIf(function () {
					return this.start_date || this.end_date
				}),
			},
		}
	},
}
</script>

<style></style>
